import React, { useEffect, useRef, useState } from 'react';
import bg from '../assets/Nursultan-with-eagle-and-Taigon-2.jpg'
import video from '../assets/bg.mp4'
import Header from "./Header";
import { useLocation } from 'react-router-dom';

/* ── Animated counter ── */
const Counter = ({ target, suffix = '' }) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const started = useRef(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([e]) => {
            if (e.isIntersecting && !started.current) {
                started.current = true;
                let start = 0;
                const duration = 1800;
                const step = Math.ceil(target / (duration / 16));
                const timer = setInterval(() => {
                    start += step;
                    if (start >= target) { setCount(target); clearInterval(timer); }
                    else setCount(start);
                }, 16);
            }
        }, { threshold: 0.5 });
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [target]);

    return <span ref={ref}>{count}{suffix}</span>;
};

/* ── Particles ── */
const Particles = () => {
    const particles = Array.from({ length: 18 }, (_, i) => ({
        id: i,
        size: Math.random() * 4 + 2,
        left: Math.random() * 100,
        delay: Math.random() * 8,
        duration: Math.random() * 10 + 8,
        color: i % 3 === 0 ? '#CE2B37' : i % 3 === 1 ? '#009246' : '#006633',
    }));
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {particles.map(p => (
                <div
                    key={p.id}
                    className="particle"
                    style={{
                        width: p.size, height: p.size,
                        left: `${p.left}%`,
                        background: p.color,
                        animationDuration: `${p.duration}s`,
                        animationDelay: `${p.delay}s`,
                    }}
                />
            ))}
        </div>
    );
};

/* ── Typewriter ── */
const useTypewriter = (words, speed = 100, pause = 2000) => {
    const [display, setDisplay] = useState('');
    const [wordIdx, setWordIdx] = useState(0);
    const [charIdx, setCharIdx] = useState(0);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const current = words[wordIdx];
        const timeout = setTimeout(() => {
            if (!deleting) {
                setDisplay(current.slice(0, charIdx + 1));
                if (charIdx + 1 === current.length) {
                    setTimeout(() => setDeleting(true), pause);
                } else {
                    setCharIdx(c => c + 1);
                }
            } else {
                setDisplay(current.slice(0, charIdx - 1));
                if (charIdx - 1 === 0) {
                    setDeleting(false);
                    setWordIdx(w => (w + 1) % words.length);
                    setCharIdx(0);
                } else {
                    setCharIdx(c => c - 1);
                }
            }
        }, deleting ? speed / 2 : speed);
        return () => clearTimeout(timeout);
    }, [charIdx, deleting, wordIdx, words, speed, pause]);

    return display;
};

const Main = () => {
    const { hash } = useLocation();
    const [isScrolled, setIsScrolled] = useState(false);
    const [loaded, setLoaded] = useState(false);

    const typed = useTypewriter(['journey', 'adventure', 'dream', 'experience'], 110, 2200);

    useEffect(() => {
        if (hash) {
            setTimeout(() => {
                const el = document.querySelector(hash);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
        }
    }, [hash]);

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        const t = setTimeout(() => setLoaded(true), 200);
        return () => clearTimeout(t);
    }, []);

    const stats = [
        { value: 9,   suffix: '+', label: 'Years of experience' },
        { value: 200, suffix: '+', label: 'Completed tours' },
        { value: 15,  suffix: '+', label: 'Destinations' },
    ];

    return (
        <div id="home" className="relative min-h-screen text-white overflow-hidden">
            <video
                className="absolute inset-0 w-full h-full object-cover scale-105"
                src={video} poster={bg}
                autoPlay muted loop playsInline
            />

            {/* Overlays */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-dark via-transparent to-transparent" />

            {/* Particles */}
            <Particles />

            {/* Green orb */}
            <div
                className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full opacity-10 animate-pulse-slow pointer-events-none"
                style={{ background: 'radial-gradient(circle, #003d1e 0%, transparent 70%)' }}
            />

            <Header isScrolled={isScrolled} />

            {/* Content */}
            <div className="relative z-10 flex flex-col justify-center min-h-screen pt-24 pb-16">
                <div className="container">
                    <div className={`flex flex-col gap-8 max-w-3xl transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-px bg-it-green opacity-90" />
                            <p className="font-unbounded text-white text-xs uppercase tracking-widest">
                                Kyrgyz Adventure Travel
                            </p>
                            <div className="w-8 h-px bg-gold opacity-90" />
                        </div>

                        <h1 className="font-unbounded font-bold text-5xl sm:text-6xl md:text-7xl leading-[1.1]">
                            Create your<br />
                            <span className="text-gold">
                                {typed}
                                <span className="typewriter-cursor" />
                            </span>
                        </h1>

                        <p className="font-manrope text-base sm:text-lg text-white/65 max-w-xl leading-relaxed">
                            Prepare to immerse yourself in the heart of Central Asia, where adventure
                            awaits at every corner, among extraordinary cultures and breathtaking landscapes.
                        </p>

                        <div className="flex flex-wrap gap-4">
                            <a href="#destinazioni" className="btn-gold">Explore tours</a>
                            <a href="#contact"      className="btn-outline">Contact us</a>
                        </div>

                        {/* Animated stats */}
                        <div className="flex flex-wrap gap-8 mt-4 pt-6 border-t border-white/10">
                            {stats.map((s, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <span className={`font-unbounded font-bold text-2xl ${i === 0 ? 'text-it-green' : i === 1 ? 'text-white' : 'text-gold'}`}>
                                        <Counter target={s.value} suffix={s.suffix} />
                                    </span>
                                    <span className="font-manrope text-xs text-white/50 uppercase tracking-wider">
                                        {s.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Scroll indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
                <span className="font-manrope text-[10px] uppercase tracking-widest text-white/40">Scroll</span>
                <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent animate-pulse-slow" />
            </div>
        </div>
    );
};

export default Main;
